import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FiUser, FiEdit3, FiSettings } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import UpdatePasswordLoggedIn from "../../components/UpdatePasswordLoggedIn";
import PortalUiSettings from "../../components/PortalUiSettings";
import axiosWrapper from "../../utils/AxiosWrapper";

const TABS = [
  { id: "overview", label: "Overview", Icon: FiUser },
  { id: "edit", label: "Edit profile", Icon: FiEdit3 },
  { id: "settings", label: "Settings", Icon: FiSettings },
];

const StudentProfileHub = ({
  profileData,
  onProfileUpdated,
  initialTab = "overview",
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() =>
    TABS.some((t) => t.id === initialTab) ? initialTab : "overview"
  );
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [savingTrack, setSavingTrack] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editForm, setEditForm] = useState({
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    emergencyName: "",
    emergencyRelationship: "",
    emergencyPhone: "",
  });

  useEffect(() => {
    const t = TABS.some((x) => x.id === initialTab) ? initialTab : "overview";
    setTab(t);
  }, [initialTab]);

  const goTab = (id) => {
    setTab(id);
    navigate(`/student?page=profile&tab=${id}`, { replace: true });
  };

  useEffect(() => {
    if (!profileData?.branchId || profileData.semester == null) return;
    const branchId = profileData.branchId._id || profileData.branchId;
    const load = async () => {
      try {
        const res = await axiosWrapper.get(
          `/method-paper-sub-subjects?branch=${branchId}&semester=${profileData.semester}&active=true`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            },
          }
        );
        if (res.data.success) setTracks(res.data.data || []);
        else setTracks([]);
      } catch {
        setTracks([]);
      }
    };
    load();
    setSelectedTrack(profileData.methodPaperSubSubject?._id || "");
  }, [profileData]);

  useEffect(() => {
    if (!profileData) return;
    const ec = profileData.emergencyContact || {};
    setEditForm({
      phone: profileData.phone || "",
      address: profileData.address || "",
      city: profileData.city || "",
      state: profileData.state || "",
      pincode: profileData.pincode || "",
      country: profileData.country || "",
      emergencyName: ec.name || "",
      emergencyRelationship: ec.relationship || "",
      emergencyPhone: ec.phone || "",
    });
  }, [profileData]);

  if (!profileData) return null;

  const methodPaperLocked = Boolean(profileData.methodPaperSubSubject)

  const token = () => localStorage.getItem("userToken");

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const saveMethodPaperTrack = async () => {
    if (methodPaperLocked) return
    try {
      setSavingTrack(true);
      const res = await axiosWrapper.patch(
        `/student/my-method-paper-track`,
        {
          methodPaperSubSubjectId: selectedTrack || null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token()}`,
          },
        }
      );
      if (res.data.success) {
        toast.success(res.data.message);
        if (typeof onProfileUpdated === "function") await onProfileUpdated();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save");
    } finally {
      setSavingTrack(false);
    }
  };

  const saveProfileFields = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(editForm.phone)) {
      toast.error("Phone must be exactly 10 digits");
      return;
    }
    if (
      editForm.emergencyPhone &&
      !/^\d{10}$/.test(editForm.emergencyPhone)
    ) {
      toast.error("Emergency phone must be 10 digits");
      return;
    }
    setSavingProfile(true);
    try {
      const body = {
        phone: editForm.phone,
        address: editForm.address,
        city: editForm.city,
        state: editForm.state,
        pincode: editForm.pincode,
        country: editForm.country,
        emergencyContact: {
          name: editForm.emergencyName,
          relationship: editForm.emergencyRelationship,
          phone: editForm.emergencyPhone,
        },
      };
      const res = await axiosWrapper.patch(`/student/my-profile`, body, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
      });
      if (res.data.success) {
        toast.success(res.data.message || "Profile updated");
        if (typeof onProfileUpdated === "function") await onProfileUpdated();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

  return (
    <div id="student-profile-hub" className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 scroll-mt-28">
      <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 shadow-soft overflow-hidden mb-6">
        <div className="flex flex-col sm:flex-row gap-6 p-6 sm:p-8 border-b border-slate-100 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-indigo-50/30 dark:from-slate-800 dark:to-slate-900/80">
          <img
            src={`${process.env.REACT_APP_MEDIA_LINK}/${profileData.profile}`}
            alt=""
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover ring-2 ring-white shadow-md mx-auto sm:mx-0 shrink-0"
          />
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50 tracking-tight truncate">
              {`${profileData.firstName} ${profileData.middleName || ""} ${profileData.lastName}`}
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              {profileData.branchId?.name} · Semester {profileData.semester}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 break-all">
              {profileData.email}
            </p>
          </div>
        </div>

        <div className="flex overflow-x-auto no-scrollbar gap-1 p-2 sm:p-3 border-b border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/60">
          {TABS.map(({ id, label, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => goTab(id)}
                className={`
                  flex items-center gap-2 shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all
                  ${
                    active
                      ? "bg-slate-900 dark:bg-indigo-600 text-white shadow-md"
                      : "text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white"
                  }
                `}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </button>
            );
          })}
        </div>

        <div className="p-5 sm:p-8">
          {tab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/50 p-4">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    Enrollment
                  </p>
                  <p className="text-lg font-bold text-slate-900 dark:text-slate-100 mt-1">
                    {profileData.enrollmentNo}
                  </p>
                  {profileData.aparId && (
                    <p className="text-sm text-slate-600 mt-2">
                      APAR:{" "}
                      <span className="font-semibold">{profileData.aparId}</span>
                    </p>
                  )}
                </div>
                <div className="rounded-xl border border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/50 p-4">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    Method paper
                  </p>
                  <p className="text-base font-semibold text-indigo-700 dark:text-indigo-300 mt-1">
                    {profileData.methodPaperSubSubject?.name || "Not set yet"}
                  </p>
                  {methodPaperLocked ? (
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                      Locked after first save — contact the office if a change is needed.
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="text-xs font-semibold text-indigo-600 mt-2 hover:underline"
                      onClick={() => goTab("settings")}
                    >
                      Choose in Settings →
                    </button>
                  )}
                </div>
                {(Number(profileData.courseFee) > 0 ||
                  (Array.isArray(profileData.feePayments) &&
                    profileData.feePayments.length > 0)) && (
                  <div className="rounded-xl border border-amber-200/80 dark:border-amber-700/50 bg-amber-50/80 dark:bg-amber-950/30 p-4 sm:col-span-2">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 uppercase tracking-wide">
                      Course fee
                    </p>
                    <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">
                      Total{" "}
                      <strong className="tabular-nums">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        }).format(Number(profileData.courseFee) || 0)}
                      </strong>
                      {" · "}
                      Paid{" "}
                      <strong className="tabular-nums text-emerald-700 dark:text-emerald-300">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        }).format(
                          typeof profileData.totalFeePaid === "number"
                            ? profileData.totalFeePaid
                            : (profileData.feePayments || []).reduce(
                                (s, p) => s + (Number(p.amount) || 0),
                                0
                              )
                        )}
                      </strong>
                      {" · "}
                      Remaining{" "}
                      <strong className="tabular-nums text-amber-900 dark:text-amber-200">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        }).format(
                          typeof profileData.feeBalanceDue === "number"
                            ? profileData.feeBalanceDue
                            : Math.max(
                                0,
                                (Number(profileData.courseFee) || 0) -
                                  (typeof profileData.totalFeePaid === "number"
                                    ? profileData.totalFeePaid
                                    : (profileData.feePayments || []).reduce(
                                        (s, p) => s + (Number(p.amount) || 0),
                                        0
                                      ))
                              )
                        )}
                      </strong>
                    </p>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 p-5 bg-white dark:bg-slate-800/40">
                <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4">Personal</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Phone</dt>
                    <dd className="font-medium text-slate-900 dark:text-slate-100">{profileData.phone}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Gender</dt>
                    <dd className="capitalize text-slate-800 dark:text-slate-200">{profileData.gender}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Blood group</dt>
                    <dd className="text-slate-800 dark:text-slate-200">{profileData.bloodGroup}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Date of birth</dt>
                    <dd className="text-slate-800 dark:text-slate-200">{formatDate(profileData.dob)}</dd>
                  </div>
                </dl>
              </div>

              <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 p-5 bg-white dark:bg-slate-800/40">
                <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4">Address</h3>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {profileData.address}, {profileData.city}, {profileData.state}{" "}
                  {profileData.pincode}, {profileData.country}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 p-5 bg-white dark:bg-slate-800/40">
                <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-4">Emergency</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Name</dt>
                    <dd className="font-medium text-slate-800 dark:text-slate-200">{profileData.emergencyContact?.name}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Relationship</dt>
                    <dd className="text-slate-800 dark:text-slate-200">{profileData.emergencyContact?.relationship}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 dark:text-slate-400">Phone</dt>
                    <dd className="font-medium text-slate-800 dark:text-slate-200">{profileData.emergencyContact?.phone}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {tab === "edit" && (
            <form onSubmit={saveProfileFields} className="space-y-6 max-w-2xl">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Update contact and address details. Academic fields (name,
                enrollment, branch) are managed by the office — reach out if
                something looks wrong.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block sm:col-span-2">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Mobile (10 digits)</span>
                  <input
                    className={`${inputClass} mt-1`}
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                    }
                    inputMode="numeric"
                    required
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs font-semibold text-slate-600">Address</span>
                  <textarea
                    className={`${inputClass} mt-1 min-h-[80px]`}
                    value={editForm.address}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, address: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">City</span>
                  <input
                    className={`${inputClass} mt-1`}
                    value={editForm.city}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, city: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">State</span>
                  <input
                    className={`${inputClass} mt-1`}
                    value={editForm.state}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, state: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Pincode</span>
                  <input
                    className={`${inputClass} mt-1`}
                    value={editForm.pincode}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, pincode: e.target.value }))
                    }
                    required
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600">Country</span>
                  <input
                    className={`${inputClass} mt-1`}
                    value={editForm.country}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, country: e.target.value }))
                    }
                    required
                  />
                </label>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4 space-y-3">
                <p className="text-sm font-semibold text-amber-900">Emergency contact</p>
                <input
                  className={inputClass}
                  placeholder="Name"
                  value={editForm.emergencyName}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, emergencyName: e.target.value }))
                  }
                />
                <input
                  className={inputClass}
                  placeholder="Relationship"
                  value={editForm.emergencyRelationship}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      emergencyRelationship: e.target.value,
                    }))
                  }
                />
                <input
                  className={inputClass}
                  placeholder="Phone (10 digits)"
                  value={editForm.emergencyPhone}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      emergencyPhone: e.target.value.replace(/\D/g, "").slice(0, 10),
                    }))
                  }
                  inputMode="numeric"
                />
              </div>
              <CustomButton type="submit" variant="primary" disabled={savingProfile}>
                {savingProfile ? "Saving…" : "Save changes"}
              </CustomButton>
            </form>
          )}

          {tab === "settings" && (
            <div className="space-y-10 max-w-2xl">
              <PortalUiSettings />

              <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 p-5 sm:p-6 bg-white dark:bg-slate-800/40">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-1">
                  Method paper (sub-subject)
                </h3>
                {methodPaperLocked ? (
                  <>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      Your selected track cannot be changed from the portal once it has been saved.
                    </p>
                    <p className="text-base font-semibold text-indigo-700 dark:text-indigo-300">
                      {profileData.methodPaperSubSubject?.name || "—"}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                      For corrections, contact the college office; staff can update your record from the admin panel.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                      Choose your track once for this branch and semester — it will be locked after you save.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
                      <div className="flex-1 min-w-0">
                        <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">
                          Sub-subject
                        </label>
                        <select
                          className={inputClass}
                          value={selectedTrack}
                          onChange={(e) => setSelectedTrack(e.target.value)}
                          disabled={!tracks.length}
                        >
                          <option value="">Not selected</option>
                          {tracks.map((t) => (
                            <option key={t._id} value={t._id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <CustomButton
                        type="button"
                        onClick={saveMethodPaperTrack}
                        disabled={
                          savingTrack || !selectedTrack || !tracks.length
                        }
                        variant="primary"
                      >
                        {savingTrack ? "Saving…" : "Save choice"}
                      </CustomButton>
                    </div>
                  </>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200/80 dark:border-slate-700 p-5 sm:p-6 bg-white dark:bg-slate-800/40">
                <UpdatePasswordLoggedIn inline onClose={() => {}} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentProfileHub;
